import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '../ui/button';
import { Trash2, Loader2, AlertTriangle } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const authHeaders = () => ({ Authorization: `Bearer ${sessionStorage.getItem('access_token')}` });

const CATEGORIES = [
  { key: 'labor_bills', label: 'Labor Bills (incl. lixeira, histórico de envios e anexos)' },
  { key: 'schedules', label: 'Agenda / Schedule (incl. rascunhos AI dos jobs)' },
  { key: 'tickets', label: 'Tickets' },
  { key: 'notifications', label: 'Notificações' },
  { key: 'daily_work', label: 'Daily Work (registros importados)' },
  { key: 'helpers', label: 'Helpers & Percentuais da equipe' },
];

// Reset de fábrica de uma equipe: ADMIN escolhe categorias e confirma digitando o nome
const TeamResetPanel = () => {
  const [teams, setTeams] = useState([]);
  const [team, setTeam] = useState('');
  const [preview, setPreview] = useState(null);
  const [checked, setChecked] = useState({});
  const [confirmText, setConfirmText] = useState('');
  const [working, setWorking] = useState(false);
  const [loadingPreview, setLoadingPreview] = useState(false);

  useEffect(() => {
    axios.get(`${API}/admin/team-reset/options`, { headers: authHeaders() })
      .then(({ data }) => setTeams(data.teams || []))
      .catch(() => setTeams([]));
  }, []);

  const loadPreview = useCallback(async (pm) => {
    if (!pm) { setPreview(null); return; }
    setLoadingPreview(true);
    try {
      const { data } = await axios.get(`${API}/admin/team-reset/${pm}/preview`, { headers: authHeaders() });
      setPreview(data.preview);
    } catch (e) {
      toast.error('Falha ao carregar contagens da equipe');
      setPreview(null);
    } finally {
      setLoadingPreview(false);
    }
  }, []);

  const onSelectTeam = (pm) => {
    setTeam(pm);
    setChecked({});
    setConfirmText('');
    loadPreview(pm);
  };

  const selectedCats = CATEGORIES.filter(c => checked[c.key]).map(c => c.key);
  const canExecute = team && selectedCats.length > 0 && confirmText === team && !working;

  const execute = async () => {
    if (!window.confirm(`Apagar PERMANENTEMENTE os registros selecionados da equipe ${team}? Essa ação não pode ser desfeita.`)) return;
    setWorking(true);
    try {
      const { data } = await axios.post(`${API}/admin/team-reset/${team}`,
        { categories: selectedCats, confirm_name: confirmText },
        { headers: authHeaders() });
      toast.success(`🗑️ Reset da equipe ${team}: ${data.total_deleted} registro(s) apagado(s)`);
      setChecked({});
      setConfirmText('');
      await loadPreview(team);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Falha ao resetar a equipe');
    } finally {
      setWorking(false);
    }
  };

  return (
    <div className="bg-red-50 border-2 border-red-300 rounded-lg p-4 mb-4" data-testid="team-reset-panel">
      <h3 className="font-bold text-red-900 flex items-center gap-2 mb-1">
        <AlertTriangle className="w-4 h-4" /> Reset de Fábrica de Equipe
      </h3>
      <p className="text-sm text-red-800 mb-3">
        Apaga permanentemente os registros escolhidos de uma equipe (ex: ao trocar o líder).
        O login do PM e o cadastro em Team Leaders são mantidos.
      </p>

      <select
        className="w-full border border-red-300 rounded-md p-2 text-sm mb-3 bg-white"
        value={team}
        onChange={(e) => onSelectTeam(e.target.value)}
        data-testid="team-reset-select"
      >
        <option value="">Selecione a equipe...</option>
        {teams.map(t => (
          <option key={t.username} value={t.username}>{t.username}{t.full_name ? ` — ${t.full_name}` : ''}</option>
        ))}
      </select>

      {loadingPreview && (
        <div className="flex items-center gap-2 text-sm text-red-700 mb-2"><Loader2 className="w-4 h-4 animate-spin" /> Contando registros...</div>
      )}

      {team && preview && !loadingPreview && (
        <div className="space-y-2 mb-3">
          {CATEGORIES.map(c => (
            <label key={c.key} className="flex items-start gap-2 text-sm bg-white border border-red-200 rounded-md p-2 cursor-pointer">
              <input
                type="checkbox"
                className="mt-0.5"
                checked={!!checked[c.key]}
                onChange={(e) => setChecked(prev => ({ ...prev, [c.key]: e.target.checked }))}
                data-testid={`team-reset-cat-${c.key}`}
              />
              <span>
                {c.label}
                <span className={`ml-1 font-semibold ${preview[c.key]?.total ? 'text-red-700' : 'text-slate-400'}`}>
                  ({preview[c.key]?.total || 0} registro{(preview[c.key]?.total || 0) === 1 ? '' : 's'})
                </span>
              </span>
            </label>
          ))}

          <div className="pt-1">
            <p className="text-xs text-red-800 mb-1">
              🔒 Para confirmar, digite <strong>{team}</strong> abaixo:
            </p>
            <input
              type="text"
              className="w-full border border-red-300 rounded-md p-2 text-sm mb-2"
              placeholder={`Digite ${team} para liberar o botão`}
              value={confirmText}
              onChange={(e) => setConfirmText(e.target.value)}
              data-testid="team-reset-confirm-input"
            />
            <Button
              variant="destructive"
              size="sm"
              disabled={!canExecute}
              onClick={execute}
              data-testid="team-reset-execute-btn"
            >
              {working ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <Trash2 className="w-4 h-4 mr-1" />}
              Apagar registros selecionados de {team}
            </Button>
          </div>
        </div>
      )}
    </div>
  );
};

export default TeamResetPanel;
